// Story 8.1 (ADO-2010), Task 5.3: the remembered ordering choice (A1.2, A1.3, A1.6).
//
// The device remembers where the signed-in user last ordered, in `localStorage`, keyed by
// tenant AND `sub`: a colleague who signs in as themselves never inherits it (Q-8.5). The
// value `{ entryKey, customerAccountCode, lastUsedAt }` expires 30 days after its last
// use, and every choice rewrites it. It is a HINT, never a permission: the entry flow
// uses it only while it is still in the offered set, and the server re-checks every
// basket call. Every read and write is wrapped, so private mode or blocked storage just
// means nothing is remembered.

import type { OrderingSelection } from './orderingChoices';

/** 30 days, matching SL1.0's re-set-on-every-request session cookie (A1.3). */
export const REMEMBERED_HINT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const KEY_PREFIX = 'shoplink.orderingChoice';

export function rememberedHintKey(tenantId: string, sub: string): string {
  return `${KEY_PREFIX}:${tenantId}:${sub}`;
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing to remove.
  }
}

/** The remembered choice, or `null` when absent, malformed, expired or unreadable. */
export function readRememberedHint(
  tenantId: string,
  sub: string,
  nowMs: number,
): OrderingSelection | null {
  const key = rememberedHintKey(tenantId, sub);
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    remove(key);
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const { entryKey, customerAccountCode, lastUsedAt } = parsed as Record<string, unknown>;
  const validCode = customerAccountCode === null || typeof customerAccountCode === 'string';
  if (typeof entryKey !== 'string' || !validCode || typeof lastUsedAt !== 'number') {
    remove(key);
    return null;
  }
  if (nowMs - lastUsedAt > REMEMBERED_HINT_TTL_MS) {
    remove(key);
    return null;
  }
  return { entryKey, customerAccountCode: customerAccountCode };
}

/** Remember `selection` as the user's last choice, stamped with `nowMs`. Never throws. */
export function writeRememberedHint(
  tenantId: string,
  sub: string,
  selection: OrderingSelection,
  nowMs: number,
): void {
  try {
    window.localStorage.setItem(
      rememberedHintKey(tenantId, sub),
      JSON.stringify({
        entryKey: selection.entryKey,
        customerAccountCode: selection.customerAccountCode,
        lastUsedAt: nowMs,
      }),
    );
  } catch {
    // Storage unavailable (private mode, quota): the choice simply is not remembered.
  }
}
